import { Breadcrumbs, Link, Skeleton, Typography } from '@deltasierra/react-components-core';
import { ChevronRight } from '@deltasierra/react-icons';
import { AssetLibraryBreadcrumb } from './types';

const RenderTypography = ({ isClickable, text }: { isClickable?: boolean; text: string }) => (
    <Typography
        sx={{
            fontSize: 20,
            maxWidth: '300px',
            overflow: 'hidden',
            paddingY: 1,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(isClickable && {
                cursor: 'pointer',
            }),
        }}
        title={text}
        variant="subtitle2"
    >
        {text}
    </Typography>
);
RenderTypography.displayName = 'RenderTypography';

export type AssetLibraryBreadcrumbsProps = {
    ariaLabel?: string;
    breadcrumbs: AssetLibraryBreadcrumb[];
    isLoading: boolean;
    onClickBreadcrumb: (value: { collectionId?: string; folderId?: string }) => void;
};

export function AssetLibraryBreadcrumbs({
    ariaLabel,
    breadcrumbs,
    isLoading,
    onClickBreadcrumb,
}: AssetLibraryBreadcrumbsProps): JSX.Element {
    return (
        <Breadcrumbs
            aria-label={ariaLabel}
            itemsBeforeCollapse={2}
            maxItems={4}
            separator={<ChevronRight />}
            sx={[
                {
                    width: 1,
                },
                isLoading && {
                    '& li': { flex: 1, maxWidth: 1, minWidth: 0 },
                },
            ]}
        >
            {isLoading ? (
                <Skeleton
                    data-cy="asset-library-breadcrumbs-skeleton"
                    sx={{ display: 'block', height: 42, width: 1 }}
                    variant="rounded"
                />
            ) : (
                breadcrumbs.map((breadcrumb, index, array) => {
                    const isLastItem = index === array.length - 1;
                    return isLastItem ? (
                        <RenderTypography key={index} text={breadcrumb.label} />
                    ) : (
                        <Link
                            color="inherit"
                            key={index}
                            underline="hover"
                            onClick={() =>
                                onClickBreadcrumb({
                                    collectionId: breadcrumb.collectionId,
                                    folderId: breadcrumb.folderId,
                                })
                            }
                        >
                            <RenderTypography isClickable text={breadcrumb.label} />
                        </Link>
                    );
                })
            )}
        </Breadcrumbs>
    );
}

AssetLibraryBreadcrumbs.displayName = 'AssetLibraryBreadcrumbs';
